import * as React from 'react'

import { cn } from './cn'

interface AccordionContextValue {
  expanded: boolean
  toggle: (event: React.SyntheticEvent) => void
}

const AccordionContext = React.createContext<AccordionContextValue>({
  expanded: false,
  toggle: () => undefined,
})

export interface AccordionProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  expanded?: boolean
  defaultExpanded?: boolean
  onChange?: (event: React.SyntheticEvent, expanded: boolean) => void
  disabled?: boolean
  disableGutters?: boolean
  elevation?: number
}

const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      expanded: expandedProp,
      defaultExpanded,
      onChange,
      disabled,
      disableGutters,
      elevation,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const [expandedState, setExpandedState] = React.useState(
      defaultExpanded ?? false,
    )
    const expanded = expandedProp ?? expandedState

    const toggle = (event: React.SyntheticEvent) => {
      setExpandedState(!expanded)
      onChange?.(event, !expanded)
    }

    return (
      <AccordionContext.Provider value={{ expanded, toggle }}>
        <div
          ref={ref}
          className={cn(
            'bg-paper text-text-primary',
            'border border-divider shadow-paper',
            'first:rounded-t-card last:rounded-b-card [&+&]:border-t-0',
            disabled && 'pointer-events-none opacity-50',
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </AccordionContext.Provider>
    )
  },
)
Accordion.displayName = 'Accordion'

export default Accordion

export const AccordionSummary = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    expandIcon?: React.ReactNode
  }
>(({ expandIcon, className, children, ...props }, ref) => {
  const { expanded, toggle } = React.useContext(AccordionContext)
  return (
    <button
      ref={ref}
      type="button"
      aria-expanded={expanded}
      onClick={toggle}
      className={cn(
        'flex w-full cursor-pointer items-center justify-between gap-3 px-5 py-4 text-left text-sm font-medium',
        'transition-colors hover:bg-black/[0.02] dark:hover:bg-white/[0.03]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40',
        className,
      )}
      {...props}
    >
      <span className="grow">{children}</span>
      {expandIcon && (
        <span
          className={cn(
            'shrink-0 text-text-secondary transition-transform duration-200',
            expanded && 'rotate-180',
          )}
        >
          {expandIcon}
        </span>
      )}
    </button>
  )
})
AccordionSummary.displayName = 'AccordionSummary'

export const AccordionDetails = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { expanded } = React.useContext(AccordionContext)
  if (!expanded) return null
  return (
    <div
      ref={ref}
      className={cn('px-5 pb-5 pt-1 text-sm animate-ps-fade-in', className)}
      {...props}
    />
  )
})
AccordionDetails.displayName = 'AccordionDetails'
